import { DangerIcon } from "@cynthai/icons";
import { Button, Code, InputErrorMessage, Otp } from "@cynthai/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { QRCode } from "react-qr-code";
import z from "zod";

import {
  apiErrorData,
  apiErrorStatus,
  extractApiExceptionCodeFromData,
  isApiError,
} from "@/core/lib/api-errors";
import { RecoveryCodes } from "@/features/auth/components/RecoveryCodes";
import { useTwoFaCompletion } from "@/features/auth/hooks/use-two-fa-completion";
import { generateTwoFa } from "@/openapi";
import { verifyTwoFaMutation } from "@/openapi/@tanstack/react-query.gen";
import { m } from "@/paraglide/messages";

export const Route = createFileRoute("/auth/two-fa")({
  component: () => <TwoFactorAuth />,
  validateSearch: z.object({
    regenerate: z.coerce.boolean().optional(),
  }),
  beforeLoad: ({ context }) => {
    if (context.user) {
      throw redirect({ to: "/" });
    }
  },
  loader: async () => {
    const twofa = await generateTwoFa();

    return {
      twofa,
    };
  },
});

const getErrorMessage = (error: unknown) => {
  if (isApiError(error)) {
    const code = extractApiExceptionCodeFromData(apiErrorData(error) ?? null);
    if (code === "TWO_FA_ALREADY_ENABLED") {
      return m.two_fa_already_enabled();
    }
  }

  if (isApiError(error) && apiErrorStatus(error) === undefined) {
    return m.two_fa_failed_to_connect();
  }

  if (
    isApiError(error) &&
    extractApiExceptionCodeFromData(apiErrorData(error) ?? null) ===
      "VALIDATION_ERROR"
  ) {
    return m.two_fa_invalid_request();
  }

  if (isApiError(error)) {
    const detail = apiErrorData(error);
    if (
      detail &&
      typeof detail === "object" &&
      "detail" in detail &&
      typeof detail.detail === "string"
    ) {
      return detail.detail;
    }
  }

  return m.two_fa_unknown_server_error();
};

function TwoFactorAuth() {
  const { regenerate } = Route.useSearch();
  const { twofa } = Route.useLoaderData();

  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const otpRef = useRef<HTMLInputElement | null>(null);
  const setupKey = twofa.data
    ? (new URL(twofa.data).searchParams.get("secret") ?? twofa.data)
    : "";

  const completion = useTwoFaCompletion();
  const verifyTwoFa = useMutation({
    ...verifyTwoFaMutation(),
    gcTime: 0,
    retry: false,
    onSuccess: (data) => {
      if (data) {
        completion.complete(data);
      } else {
        setErrorMessage(m.two_fa_verify_error());
      }
    },
    onError: (error) => {
      setErrorMessage(getErrorMessage(error));
    },
  });
  const { mutate: verifyTwoFaMutate } = verifyTwoFa;

  const handleComplete = useCallback(
    (value?: string) => {
      if (
        value?.length === 6 &&
        !verifyTwoFa.isPending &&
        !completion.recoveryCodes
      ) {
        verifyTwoFaMutate({ body: { otp: value } });
      }
    },
    [verifyTwoFaMutate, verifyTwoFa.isPending, completion.recoveryCodes],
  );

  if (completion.recoveryCodes) {
    return (
      <RecoveryCodes
        codes={completion.recoveryCodes}
        onContinue={() => {
          verifyTwoFa.reset();
          void completion.continueAfterSaving();
        }}
      />
    );
  }

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-4 rounded-lg">
      <p className="text-text-primary mb-4 text-2xl font-semibold">
        {regenerate ? m.two_fa_regenerate_title() : m.two_fa_setup_title()}
      </p>

      {twofa.data ? (
        <div className="space-y-4">
          <p className="text-text-secondary">
            {regenerate ? (
              <>
                <span>{m.two_fa_regenerate_instructions_p1()}</span>
                <span className="font-semibold">
                  {m.two_fa_regenerate_instructions_p2()}
                </span>
                <br />
              </>
            ) : (
              <>
                {m.two_fa_scan_qr_code()}
                <br />
                {m.two_fa_enter_setup_key()} <br />{" "}
              </>
            )}
          </p>
          <div className="flex justify-center rounded-lg bg-white p-6">
            <QRCode
              value={twofa.data}
              bgColor="#FFFFFF"
              fgColor="#000000"
              size={250}
            />
          </div>
          <div className="flex flex-row items-center gap-2">
            <Code size="sm" variant="2fa">
              {setupKey}
            </Code>
            <Button
              color="primary"
              variant="ghost"
              size="sm"
              onPress={() => {
                void navigator.clipboard.writeText(setupKey);
              }}
              className="mx-auto"
            >
              {m.two_fa_copy_code()}
            </Button>
          </div>
          <p className="text-text-secondary text-sm">
            {m.two_fa_afterwards_prompt()}{" "}
            <span className="font-semibold">{m.two_fa_verify()}</span>
          </p>
          <Otp
            autoFocus
            aria-label={m.two_fa_otp_label()}
            disabled={verifyTwoFa.isPending}
            onComplete={handleComplete}
            ref={otpRef}
          />
          <Button
            isLoading={verifyTwoFa.isPending}
            onPress={() => {
              if (otpRef.current) {
                handleComplete(otpRef.current.value);
              }
            }}
            color="primary"
            size="lg"
            className="w-full"
          >
            {m.two_fa_verify_button_label()}
          </Button>
          {errorMessage && (
            <InputErrorMessage>{errorMessage}</InputErrorMessage>
          )}
        </div>
      ) : (
        <>
          <p
            className={`bg-secondary text-danger mb-4 flex items-center gap-2 rounded-lg p-4 text-sm`}
          >
            <DangerIcon height={24} /> {getErrorMessage(twofa.error)}
          </p>
          <Button
            color="primary"
            size="sm"
            onPress={() => {
              navigate({ to: "/auth/sign-in" });
            }}
          >
            {m.two_fa_back_to_sign_in()}
          </Button>
        </>
      )}
    </div>
  );
}
